/**
 * Introductory BCP Document Transformation Utilities
 *
 * Extracts sections from BCP Markdown documents for form population
 */

export interface IntroBCPFormData {
  company_info?: string;
  business_continuity?: string;
  basic_policy?: string;
  expected_damage?: string;
  earthquake_response?: string;
  disaster_response_team?: string;
  priority_business?: string; // 4-1
  shortage_items?: string; // 4-2
  checklist?: string;
}

/**
 * Extract a specific section from Markdown by section number
 *
 * @param markdown - The BCP document in Markdown format
 * @param sectionNumber - Section number (e.g., "2-1", "4-1", "4-2")
 * @returns Extracted content (without heading) or null if not found
 */
export function extractSectionByNumber(
  markdown: string,
  sectionNumber: string
): string | null {
  if (!markdown) {
    return null;
  }

  // Escape special regex characters in section number
  const escapedSection = sectionNumber.replace(/-/g, '-');

  // Pattern to match section heading and content
  // Matches: ## 4-1 重点事業の選定・復旧計画
  //          Content here...
  //          (until next ## heading or end of document)
  const sectionPattern = new RegExp(
    `##\\s*${escapedSection}\\s+.*?\\n([\\s\\S]*?)(?=\\n##\\s*|$)`,
    'i'
  );

  const match = markdown.match(sectionPattern);

  if (match && match[1]) {
    return match[1].trim();
  }

  return null;
}

/**
 * Transform BCP Markdown document into form data
 *
 * Extracts各セクションfrom the generated BCP document
 *
 * @param bcpDocument - API response bcp_document (Markdown format)
 * @returns Form data with extracted sections
 */
export function transformIntroBCPDocument(
  bcpDocument: string
): IntroBCPFormData {
  const formData: IntroBCPFormData = {};

  if (!bcpDocument) {
    return formData;
  }

  // Extract section 2-1: Expected damage at location
  const section21 = extractSectionByNumber(bcpDocument, '2-1');
  if (section21) {
    formData.expected_damage = section21;
  }

  // Extract section 4-1: Priority business planning
  const section41 = extractSectionByNumber(bcpDocument, '4-1');
  if (section41) {
    formData.priority_business = section41;
  }

  // Extract section 4-2: Shortage items
  const section42 = extractSectionByNumber(bcpDocument, '4-2');
  if (section42) {
    formData.shortage_items = section42;
  }

  // Extract section 5-1: Checklist (if needed)
  const section51 = extractSectionByNumber(bcpDocument, '5-1');
  if (section51) {
    formData.checklist = section51;
  }

  return formData;
}
